// https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/using-react/
// https://www.algolia.com/doc/ui-libraries/autocomplete/guides/creating-a-renderer/

import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import "@algolia/autocomplete-theme-classic";
import { getAlgoliaResults } from "@algolia/autocomplete-js";
import GlobalSearchItem from "./global-search-item.js";
import searchClient from "../../../utils/algolia/algoliaClient.js";
import { IconButton, Input, InputAdornment, Typography } from "@mui/material";
import { createAutocomplete } from "@algolia/autocomplete-core";
import { IoClose, IoSearch } from "react-icons/io5";
import "./global-search.scss";
import { useMuiScreenSizes } from "../../../hooks/useMuiScreenSizes.js";

const globalSearchIndexName = process.env.REACT_APP_ALGOLIA_GLOBAL_SEARCH_INDEX;

function GlobalSearchAutocomplete({ handleCloseSearch }) {
  // (1) Create a React state.
  const [autocompleteState, setAutocompleteState] = useState({});
  const navigate = useNavigate();
  
  const queryRef = useRef("");
  const inputRef = useRef(null);

  const { isScreenMedium } = useMuiScreenSizes();

  const handleSubmit = () => {
    if (queryRef.current) {
      navigate("/global-search-result?query=" + queryRef.current);
    }
    handleCloseSearch();
  };

  const autocomplete = useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }) {
          // (2) Synchronize the Autocomplete state with the React state.
          queryRef.current = state.query;
          setAutocompleteState(state);
        },
        openOnFocus: true,
        getSources() {
          return [
            // (3) Use an Algolia index source.
            {
              sourceId: "GlobalSearch",
              getItemInputValue({ item }) {
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: globalSearchIndexName,
                      params: {
                        query,
                        hitsPerPage: 10,
                        highlightPreTag: "<mark>",
                        highlightPostTag: "</mark>"
                      }
                    }
                  ]
                });
              },
              getItemUrl({ item }) {
                return item.url;
              }
            }
          ];
        },
        onSubmit() {
          handleSubmit();
        }
      }),
    []
  );

  return (
    <div
      className="aa-Autocomplete d-flex flex-column"
      style={{ height: "-webkit-fill-available" }}
      {...autocomplete.getRootProps({})}
    >
      <form className="w-100" {...autocomplete.getFormProps({ inputElement: inputRef.current })}>
        <Input
          ref={inputRef}
          startAdornment={
            <InputAdornment position="start">
              <IoSearch size={30} color="#003366" className="mx-2" />
            </InputAdornment>
          }
          endAdornment={
            <IconButton onClick={handleCloseSearch}>
              <IoClose size={30} />
            </IconButton>
          }
          sx={{
            padding: isScreenMedium ? "15px 0 10px" : "20px 0",
            margin: "0 10px",
            width: "-webkit-fill-available",
            "::before": {
              borderBottom: "1px solid #00336624"
            },
            "&:hover:not(.Mui-disabled, .Mui-error)::before": {
              borderBottom: "1px solid #00336624"
            },
            "&::after": {
              borderBottom: "none"
            }
          }}
          inputProps={{ sx: { fontSize: "large", padding: 0 } }}
          {...autocomplete.getInputProps({
            placeholder: "Search ATC website...",
            autoFocus: true,
            type: "text"
          })}
        />
      </form>

      <div className="flex-grow-1 mt-1" style={{ overflowY: "auto" }} {...autocomplete.getPanelProps({})}>
        {autocompleteState.isOpen && (
          <>
            {autocompleteState.collections.map((collection, index) => {
              const { source, items } = collection;

              return (
                <div key={`source-${index}`} className="aa-Source">
                  {items.length > 0 && (
                    <ul className="aa-List" {...autocomplete.getListProps()}>
                      {items.map((item, i) => (
                        <div key={i} className="me-3 my-2">
                          <GlobalSearchItem
                            key={i}
                            hit={item}
                            handleClick={handleCloseSearch}
                            {...autocomplete.getItemProps({
                              item,
                              source
                            })}
                          />
                        </div>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}

            {queryRef.current && (
              <div className="d-flex px-3 pt-3">
                <span className="me-2 fs-5" style={{ marginTop: "2px" }}>
                  ⮞
                </span>
                <Typography variant="overline" style={{ fontSize: "smaller", lineHeight: 1.57 }}>
                  Press <b>Enter</b> to see all results for <i>"{queryRef.current}"</i>
                </Typography>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

GlobalSearchAutocomplete.propTypes = {
  handleCloseSearch: PropTypes.func.isRequired
};

export default GlobalSearchAutocomplete;
